import CheckCircleIcon from '@material-design-icons/svg/filled/check_circle.svg?react';
import ErrorIcon from '@material-design-icons/svg/filled/error.svg?react';
import InfoIcon from '@material-design-icons/svg/filled/info.svg?react';
import WarningIcon from '@material-design-icons/svg/filled/warning.svg?react';
import { useState } from 'react';

interface EventItem {
  id: string;
  eventType: string;
  payload: unknown;
  moduleResult?: string;
  reviewStatus?: string;
  errorCode?: string;
  errorMessage?: string;
  clientTimestamp: string;
  serverTimestamp: string;
}

interface KybEventTimelineProps {
  events: EventItem[];
}

export function KybEventTimeline({ events }: KybEventTimelineProps) {
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());

  const toggleEvent = (eventId: string) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const getEventIcon = (event: EventItem) => {
    if (event.moduleResult === 'GREEN') {
      return <CheckCircleIcon className="size-5 text-green-400" />;
    }
    if (event.moduleResult === 'RED' || event.errorCode) {
      return <ErrorIcon className="size-5 text-red-400" />;
    }
    if (event.moduleResult === 'YELLOW') {
      return <WarningIcon className="size-5 text-yellow-400" />;
    }
    return <InfoIcon className="size-5 text-blue-400" />;
  };

  const getEventTitle = (eventType: string) => {
    const titles: Record<string, string> = {
      OnReady: 'SDK Ready',
      OnInitialized: 'SDK Initialized',
      OnStepInitiated: 'Step Initiated',
      OnStepCompleted: 'Step Completed',
      OnLivenessCompleted: 'Liveness Check Completed',
      OnApplicantLoaded: 'Applicant Loaded',
      OnApplicantSubmitted: 'Applicant Submitted',
      OnApplicantResubmitted: 'Applicant Resubmitted',
      OnApplicantStatusChanged: 'Status Changed',
      OnApplicantActionLoaded: 'Action Loaded',
      OnApplicantActionSubmitted: 'Action Submitted',
      OnApplicantActionStatusChanged: 'Action Status Changed',
      OnApplicantActionCompleted: 'Action Completed',
      ModuleResultPresented: 'Module Result',
      OnError: 'Error',
      OnUploadError: 'Upload Error',
      OnUploadWarning: 'Upload Warning',
      OnResize: 'UI Resized',
      OnNavigationUiControlsStateChanged: 'Navigation Changed',
      OnVideoIdentCallStarted: 'Video Call Started',
      OnVideoIdentModeratorJoined: 'Moderator Joined',
      OnVideoIdentCompleted: 'Video Call Completed',
    };
    return titles[eventType] || eventType;
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (events.length === 0) {
    return (
      <div className="text-center text-neutral-400 py-4">
        No events recorded
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-neutral-700"></div>
      <div className="space-y-4">
        {events.map((event) => (
          <div key={event.id} className="relative flex items-start">
            <div className="relative z-10 flex items-center justify-center size-12 bg-neutral-900 rounded-full border-2 border-neutral-700">
              {getEventIcon(event)}
            </div>

            <div className="ml-4 flex-1 bg-neutral-900 rounded-lg p-3 border border-neutral-700">
              <div
                className="flex items-start justify-between cursor-pointer"
                onClick={() => toggleEvent(event.id)}
              >
                <div className="flex-1">
                  <div className="text-sm font-medium text-white">
                    {getEventTitle(event.eventType)}
                  </div>
                  <div className="text-xs text-neutral-400 mt-1">
                    {formatTimestamp(event.clientTimestamp)}
                  </div>
                  {event.reviewStatus && (
                    <div className="text-xs text-neutral-300 mt-1">
                      Status: {event.reviewStatus}
                    </div>
                  )}
                  {event.errorMessage && (
                    <div className="text-xs text-red-400 mt-1">
                      Error: {event.errorMessage}
                    </div>
                  )}
                </div>
                <div className="ml-2 text-neutral-400">
                  <svg
                    className={`w-4 h-4 transition-transform ${
                      expandedEvents.has(event.id) ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-label={
                      expandedEvents.has(event.id) ? 'Collapse' : 'Expand'
                    }
                  >
                    <title>
                      {expandedEvents.has(event.id) ? 'Collapse' : 'Expand'}
                    </title>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              {expandedEvents.has(event.id) && (
                <div className="mt-3 pt-3 border-t border-neutral-800">
                  <div className="text-xs text-neutral-400 mb-2">
                    Event Payload:
                  </div>
                  <pre className="bg-neutral-950 rounded p-2 text-xs text-neutral-300 overflow-x-auto max-h-64 overflow-y-auto">
                    {JSON.stringify(event.payload, null, 2)}
                  </pre>
                  <div className="mt-2 text-xs text-neutral-500">
                    Server time: {formatTimestamp(event.serverTimestamp)}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
